import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { LenderService } from '@shared/service/lender/lender.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { getFlowInfo, FlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { WorkflowService } from '@workflow/service/workflow/workflow.service';
import { OLAUITemplates, OLA_WelcomePage_Title, OLA_SecuredLoan_Title, coBorrowersPathForOLA } from '../types/constants';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { StatefulService } from '@app/service/stateful.service';
import { FlowInstance } from '@workflow/class/flow.service';
import { PageInstance } from '@workflow/class/page.service';

/**
 * A helper class for all the logics used in page component
 */
@Injectable({
  providedIn: 'root'
})
export class OlaPageHelper {
  private readonly statefulService = inject(StatefulService);

  /** boolean to show remove joint applicant confirmation modal */
  public showDeleteJointApplicantModal: boolean;

  /** Flow instance */
  public get flow(): FlowInstance {
    return this.workflowService.currentFlow;
  }

  /** Page instance */
  public get page(): PageInstance {
    return this.workflowService.currentPage;
  }

  /** Previous Page instance */
  public get previousPage(): PageInstance {
    return this.workflowService.currentPage?.previousPage;
  }

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly userService: UserService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly workflowService: WorkflowService,
    private readonly lenderService: LenderService
  ) {}

  /** Showing Workflow Navigation if ola user registered or logged in */
  public get showOLAWorkFlowNavigation(): boolean {
    // if (this.flow.id === 'ola') {
    //   return this.isAuthenticatedUser;
    // } else {
    //   return true;
    // }
    return true;
  }

  private get isAuthenticatedUser(): boolean {
    return this.userService && this.userService.isAuthenticated;
  }

  public setOlaFlow(): void {
    this.userService.flowType = 'OLA';
  }

  public ValidateAllOLAPage(): void {
    if (this.flow.id.indexOf('ola') !== -1) {
      /* below code Validate all pages if membership changed */
      this.statefulService.wrap('mem:ValidateAllPages').afterPatch(() => {
        this.flow.pages.forEach((page) => {
          page.cards.forEach((c) => {
            c.fields.forEach((f) => f.revalidate());
          });
        });
      });
    }
  }

  /**
   * Removes the co-borrower and navigates back to the previous page.
   *
   * @param currentPageId - The ID of the current page from which navigation is occurring.
   */
  public removeCoBorrowerAndGoBack = (currentPageId: string): void => {
    this.statefulService.arrayTruncate(coBorrowersPathForOLA, () => {
      this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value = false;
      const previousPage = this.workflowService.currentFlow.pages.find((x) => x.id === currentPageId);
      if (!previousPage) return;
      const flowPath = this.workflowService.getFlowPathWithPrefix(this.workflowService.currentFlow);
      this.router.navigateByUrl(`${flowPath}/${previousPage.path}`);
    });
  };

  public checkLoanTypeChange(): boolean {
    const newLoanGroup = this.statefulService.wrap('ss:ola-new-loan-group').valueString;
    const newLoanType = this.statefulService.wrap('ss:ola-new-loan-type').value;
    const newUITemplate = this.statefulService.wrap('ss:ola-new-ui-template').value;

    const prevLoanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value;
    const prevLoanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value;

    const uiTemplateName = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;

    if (
      prevLoanGroup &&
      prevLoanGroup !== newLoanGroup &&
      // eslint-disable-next-line eqeqeq
      ((uiTemplateName && newUITemplate == OLAUITemplates.PersonalTemplate) || prevLoanType !== newLoanType || prevLoanType === newLoanType)
    ) {
      return true;
    }

    if (prevLoanGroup && newLoanGroup && prevLoanGroup === newLoanGroup && prevLoanType && newLoanType && prevLoanType !== newLoanType) {
      return true;
    }

    if (prevLoanGroup && newLoanGroup && prevLoanGroup === newLoanGroup && newLoanType && prevLoanType && prevLoanType !== newLoanType) {
      return true;
    }

    if (!prevLoanGroup && !prevLoanType) {
      return false;
    }
  }

  public olaFlowInitializer(): void {
    const preShowPages = ['ola-consumer-interstitial', 'ola-econsent-splash-screen'];
    if (!preShowPages.includes(this.page.id)) {
      this.loadNavbarTitle();
      this.ValidateAllOLAPage();
      this.olaNavigationInProgressApp();
    }
  }

  public loadNavbarTitle(): void {
    this.updateNavBarTitle();
  }

  public olaNavigationInProgressApp(): void {
    const currentPageIndex = this.flow.pages.findIndex((t) => t.id === this.page?.id);
    const flowPath = this.workflowService?.currentFlow
      ? this.workflowService.getFlowPathWithPrefix(this.workflowService.currentFlow)
      : this.flow.id;
    const isLoanGroupChanged = this.statefulService.wrap('ss:is-loangroup-got-changed').value;
    const extraData = this.statefulService.wrap('ola:extraData').valueString;
    // Bug #244854
    // if the loangroup is empty and pageid is not 'ola-consumer-loan-type'. we are redirecting it to the 'ola-consumer-loan-type' page.
    const selectedloanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    if (!selectedloanGroup && this.page.id !== 'ola-consumer-loan-type') {
      this.router.navigate(['ola/ola-consumer-loan-type']);
      return;
    }

    if (this.previousPage && !this.previousPage?.helper?.isPageComplete) {
      this.statefulService.wrap('ss:oao-first-time-navigation').value = true;
    }
    const isFirstOLANavigation = this.statefulService.wrap('ss:oao-first-time-navigation').value;

    // eslint-disable-next-line eqeqeq
    // TODO: We may not need loan detail redirection here on page load.
    if (isLoanGroupChanged === true || isLoanGroupChanged === 'true') {
      this.redirectToLoanDetails();
    } else if (isFirstOLANavigation) {
      const incompletedPages = this.flow.pages.filter(
        (t, i) =>
          i < currentPageIndex && t.visible && !t.isActionPage && (!t.helper.isPageComplete || !this.isExtradataCompleted(t, extraData))
      );
      if (incompletedPages && incompletedPages.length > 0 && incompletedPages[0]) {
        const navigateUrl = incompletedPages[0].path;
        console.error('Loopback - Navigating to page:', navigateUrl);
        /* When preserveQueryParam is set to true redirect page with query params */
        const extras: NavigationExtras = {};
        if (incompletedPages[0].config.preserveQueryParam) {
          extras.queryParams = this.route.snapshot.queryParams;
        }
        this.router.navigate([`${flowPath}/${navigateUrl}`], extras);
      }
    }
    if (isFirstOLANavigation) {
      this.statefulService.wrap('ss:oao-first-time-navigation').value = false;
    }
  }

  /**
   * Redirect to OLA Pages
   * @param extraData string
   * @param flowPath string
   * @returns void
   */
  public redirectToOLAPages(extraData: string, flowPath: string): void {
    const visibledPages = this.flow.pages.filter((t) => t.visible && t.id !== 'ext-registration');
    if (!visibledPages) {
      return;
    }

    for (const element of visibledPages) {
      const page = element;
      if (page.id === this.page.id) {
        break;
      }
      const isExtradataCompleted = this.isExtradataCompleted(page, extraData);
      if (!isExtradataCompleted && page.id !== 'ola-consumer-interstitial') {
        this.router.navigate([`${flowPath}/${page.path}`]);
        break;
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private isExtradataCompleted({ id, config }: PageInstance, extraData: string): boolean {
    if (!extraData) {
      return false;
    }

    const extraDataObj = JSON.parse(extraData);
    return !(
      !extraDataObj[id] ||
      !extraDataObj[id].isVisited ||
      !extraDataObj[id].state ||
      extraDataObj[id].state?.toLowerCase() !== 'completed'
    );
  }

  private redirectToLoanDetails(): void {
    this.statefulService.wrap('ss:is-app-has-no-template').value = false;
    this.statefulService.wrap('ss:is-loangroup-got-changed').value = false;
    const uiTemplate = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
    let templates = [];
    if (this.adminPortalFeaturesService?.config?.olaConfiguration) {
      templates = this.adminPortalFeaturesService?.config?.olaConfiguration?.masterUITemplates;
    }
    const template = templates.filter((t) => t.templateName.toLowerCase() === uiTemplate.toLowerCase())[0];
    const flowInfo = getFlowInfo(this.flow);
    const path = this.getOLAFirstVisiblePage(this.flow, flowInfo, template.path);
    this.router.navigate(['app/' + path]);
  }

  /** getOLAFirstInvalidPage */
  private getOLAFirstVisiblePage(flow: FlowInstance, flowInfo: FlowInfo, workflowPrefix: string): string {
    let objFirstVisiblePage = 'ola/ola-consumer-loan-type';
    if (flow.id.toLowerCase() === workflowPrefix) {
      const viisblePages = flow.pages.filter((t) => t.visible && t.id !== 'ola-consumer-loan-type');
      if (viisblePages && viisblePages.length > 0) {
        objFirstVisiblePage = `${flowInfo.configPath}/${viisblePages[0].path}`;
      }
    }
    return objFirstVisiblePage;
  }

  public updateNavBarTitle(): void {
    const loanGroupName = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;
    const loanProductName: string = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    const uiTemplateName = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;

    if (!loanGroupName || !uiTemplateName) {
      return;
    }

    if (
      this.adminPortalFeaturesService?.config?.olaConfiguration &&
      this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups
    ) {
      const consumerLoanGroups = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedLoanGroup: any = consumerLoanGroups.filter((t) => t.loanGroup.toLowerCase() === loanGroupName.toLowerCase())[0];

      if ((selectedLoanGroup && !selectedLoanGroup.isLoanGroupHasProduct) || loanProductName) {
        if (this.page?.id === 'ola-consumer-loan-type' || this.flow?.id === 'ola') {
          this.flow.config.title = OLA_WelcomePage_Title;
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedLoanProduct: any = selectedLoanGroup.loanProducts.filter(
          (t) => !selectedLoanGroup.isLoanGroupHasProduct || t.productName === loanProductName
        )[0];

        if (!selectedLoanProduct) {
          this.workflowService.getFirstFlow();
          return;
        }

        if (uiTemplateName.toLowerCase() === OLAUITemplates.SecuredLoanCardTemplate) {
          this.flow.config.title = OLA_SecuredLoan_Title;
        } else if (uiTemplateName.toLowerCase() === OLAUITemplates.CreditCardTemplate) {
          this.flow.config.title = selectedLoanProduct.displayName;
        } else if (selectedLoanGroup.isLoanGroupHasProduct) {
          this.flow.config.title = selectedLoanGroup.displayName + ' ' + selectedLoanProduct.displayName;
        } else {
          this.flow.config.title = selectedLoanProduct.displayName;
        }
      }
    }
  }
}
